/**
 * Incident Response Playbook Engine (FE mirror of BE playbook engine)
 * Feature: US-157 (Incident Response Playbooks for US Data Breaches)
 * Task: T-157-001
 */

import { landlordApi } from '../../services/api';

// ── Types ───────────────────────────────────────────────────────────────────

export type IncidentType = 'CCPA_BREACH' | 'TX_521_BREACH' | 'GENERAL_INCIDENT';

export type IncidentStage =
  | 'DETECTION'
  | 'CONTAINMENT'
  | 'ASSESSMENT'
  | 'NOTIFICATION'
  | 'REMEDIATION'
  | 'POST_MORTEM';

export type IRRole =
  | 'INCIDENT_COMMANDER'
  | 'LEGAL_COUNSEL'
  | 'TECHNICAL_LEAD'
  | 'COMMUNICATIONS_LEAD'
  | 'DPO';

export type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface AuditEntry {
  action: string;
  actorRole: IRRole;
  details: string;
  timestamp: string;
}

export interface Playbook {
  id: string;
  type: IncidentType;
  severity: Severity;
  stage: IncidentStage;
  title: string;
  description: string;
  createdAt: string;
  roles: Record<IRRole, string | null>;
  auditLog: AuditEntry[];
  deadline?: string;
}

// ── Constants ───────────────────────────────────────────────────────────────

export const INCIDENT_TYPES: IncidentType[] = [
  'CCPA_BREACH',
  'TX_521_BREACH',
  'GENERAL_INCIDENT',
];

export const INCIDENT_STAGES: IncidentStage[] = [
  'DETECTION',
  'CONTAINMENT',
  'ASSESSMENT',
  'NOTIFICATION',
  'REMEDIATION',
  'POST_MORTEM',
];

export const IR_ROLES: IRRole[] = [
  'INCIDENT_COMMANDER',
  'LEGAL_COUNSEL',
  'TECHNICAL_LEAD',
  'COMMUNICATIONS_LEAD',
  'DPO',
];

export const INCIDENT_SEVERITY: Severity[] = [
  'LOW',
  'MEDIUM',
  'HIGH',
  'CRITICAL',
];

export const VALID_STAGE_TRANSITIONS: Record<IncidentStage, IncidentStage[]> = {
  DETECTION: ['CONTAINMENT'],
  CONTAINMENT: ['ASSESSMENT'],
  ASSESSMENT: ['NOTIFICATION'],
  NOTIFICATION: ['REMEDIATION'],
  REMEDIATION: ['POST_MORTEM'],
  POST_MORTEM: [],
};

export const STAGE_DESCRIPTIONS: Record<IncidentStage, { title: string; summary: string }> = {
  DETECTION: {
    title: 'Detection & Triage',
    summary: 'Identify the breach, determine affected systems, and establish the detection timestamp.',
  },
  CONTAINMENT: {
    title: 'Containment & Isolation',
    summary: 'Isolate compromised endpoints, rotate credentials, and halt ongoing data exfiltration.',
  },
  ASSESSMENT: {
    title: 'Forensic Assessment',
    summary: 'Analyze compromised data types, count affected residents, and assess statutory thresholds.',
  },
  NOTIFICATION: {
    title: 'Statutory Notification',
    summary: 'Dispatch notices to affected consumers, Attorneys General, and regulatory reporting bodies.',
  },
  REMEDIATION: {
    title: 'Remediation & Hardening',
    summary: 'Patch identified vulnerabilities, strengthen access controls, and verify system integrity.',
  },
  POST_MORTEM: {
    title: 'Post-Mortem & Review',
    summary: 'Conduct root-cause analysis, document statutory compliance, and update response procedures.',
  },
};

export const STORAGE_KEY = 'compliance_security_playbooks';

// ── Deadline Helpers ────────────────────────────────────────────────────────

export function calculateDeadline(type: IncidentType, fromDate: Date = new Date()): string | undefined {
  const d = new Date(fromDate);
  if (type === 'CCPA_BREACH') {
    d.setDate(d.getDate() + 45);
    return d.toISOString();
  }
  if (type === 'TX_521_BREACH') {
    d.setDate(d.getDate() + 60);
    return d.toISOString();
  }
  return undefined;
}

export function calculateDaysRemaining(deadline?: string): number | null {
  if (!deadline) return null;
  const target = new Date(deadline).getTime();
  const diff = target - Date.now();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

export function getNextStage(currentStage: IncidentStage): IncidentStage | null {
  const allowed = VALID_STAGE_TRANSITIONS[currentStage];
  return allowed && allowed.length > 0 ? allowed[0] : null;
}

// ── State Managers ──────────────────────────────────────────────────────────

export function createPlaybook(
  type: IncidentType,
  severity: Severity,
  title: string,
  description: string,
  options?: { id?: string; createdAt?: string; deadline?: string }
): Playbook {
  const now = options?.createdAt || new Date().toISOString();
  const id = options?.id || `pb-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  const deadline = options?.deadline !== undefined ? options.deadline : calculateDeadline(type, new Date(now));

  const roles: Record<IRRole, string | null> = {
    INCIDENT_COMMANDER: null,
    LEGAL_COUNSEL: null,
    TECHNICAL_LEAD: null,
    COMMUNICATIONS_LEAD: null,
    DPO: null,
  };

  const initialAudit: AuditEntry = {
    action: 'PLAYBOOK_CREATED',
    actorRole: 'INCIDENT_COMMANDER',
    details: `Created ${type} incident: "${title}" (Severity: ${severity})`,
    timestamp: now,
  };

  return {
    id,
    type,
    severity,
    stage: 'DETECTION',
    title,
    description,
    createdAt: now,
    roles,
    auditLog: [initialAudit],
    deadline,
  };
}

export const createPlaybookInstance = createPlaybook;

export function transitionStage(playbook: Playbook, nextStage: IncidentStage): Playbook {
  const allowed = VALID_STAGE_TRANSITIONS[playbook.stage] || [];
  if (!allowed.includes(nextStage)) {
    throw new Error(`Invalid stage transition from ${playbook.stage} to ${nextStage}`);
  }

  const timestamp = new Date().toISOString();
  const auditEntry: AuditEntry = {
    action: 'STAGE_TRANSITION',
    actorRole: 'INCIDENT_COMMANDER',
    details: `Transitioned stage from ${playbook.stage} to ${nextStage}`,
    timestamp,
  };

  return {
    ...playbook,
    stage: nextStage,
    auditLog: [...playbook.auditLog, auditEntry],
  };
}

export function assignRole(playbook: Playbook, role: IRRole, userId: string | null): Playbook {
  if (!IR_ROLES.includes(role)) {
    throw new Error(`Invalid role: ${role}`);
  }

  const timestamp = new Date().toISOString();
  const updatedRoles: Record<IRRole, string | null> = {
    ...playbook.roles,
    [role]: userId,
  };

  const auditEntry: AuditEntry = {
    action: 'ROLE_ASSIGNMENT',
    actorRole: 'INCIDENT_COMMANDER',
    details: userId ? `Assigned role ${role} to ${userId}` : `Unassigned role ${role}`,
    timestamp,
  };

  return {
    ...playbook,
    roles: updatedRoles,
    auditLog: [...playbook.auditLog, auditEntry],
  };
}

export function addAuditLogEntry(
  playbook: Playbook,
  entry: Omit<AuditEntry, 'timestamp'> & { timestamp?: string }
): Playbook {
  const fullEntry: AuditEntry = {
    ...entry,
    timestamp: entry.timestamp || new Date().toISOString(),
  };

  return {
    ...playbook,
    auditLog: [...playbook.auditLog, fullEntry],
  };
}

export function getPlaybookSummary(playbook: Playbook) {
  const assignedRolesCount = Object.values(playbook.roles).filter(Boolean).length;
  const daysRemaining = calculateDaysRemaining(playbook.deadline);

  return {
    id: playbook.id,
    type: playbook.type,
    stage: playbook.stage,
    severity: playbook.severity,
    title: playbook.title,
    assignedRolesCount,
    totalRoles: Object.keys(playbook.roles).length,
    auditCount: playbook.auditLog.length,
    daysRemaining,
  };
}

// ── LocalStorage Persistence ────────────────────────────────────────────────

export function getPlaybooks(): Playbook[] {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.error('Failed to read playbooks from localStorage', err);
    return [];
  }
}

export function getPlaybook(id: string): Playbook | undefined {
  const list = getPlaybooks();
  return list.find((p) => p.id === id);
}

export function savePlaybook(playbook: Playbook): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const list = getPlaybooks();
    const idx = list.findIndex((p) => p.id === playbook.id);
    if (idx >= 0) {
      list[idx] = playbook;
    } else {
      list.push(playbook);
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (err) {
    console.error('Failed to save playbook to localStorage', err);
  }
}

export function deletePlaybook(id: string): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const list = getPlaybooks();
    const updated = list.filter((p) => p.id !== id);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.error('Failed to delete playbook from localStorage', err);
  }
}

// ── Backend API Clients ─────────────────────────────────────────────────────

export const ccpaApi = {
  getMeta: () => landlordApi.get('/api/texas/ccpa/meta'),
  getResidency: () => landlordApi.get('/api/texas/ccpa/residency'),
  getSnapshot: () => landlordApi.get('/api/texas/ccpa/snapshot'),
  getViolations: () => landlordApi.get('/api/texas/ccpa/violations'),
  check: (payload: any) => landlordApi.post('/api/texas/ccpa/check', payload),
  stripeCheck: () => landlordApi.get('/api/texas/ccpa/stripe-check'),
};

export const dsrApi = {
  getMeta: () => landlordApi.get('/api/texas/ccpa/dsr/meta'),
  getRequests: () => landlordApi.get('/api/texas/ccpa/dsr/requests'),
  getRequest: (id: string) => landlordApi.get(`/api/texas/ccpa/dsr/requests/${id}`),
  verifyRequest: (id: string, payload: any) => landlordApi.post(`/api/texas/ccpa/dsr/requests/${id}/verify`, payload),
  extendRequest: (id: string, payload: any) => landlordApi.post(`/api/texas/ccpa/dsr/requests/${id}/extend`, payload),
  fulfillRequest: (id: string, payload: any) => landlordApi.post(`/api/texas/ccpa/dsr/requests/${id}/fulfill`, payload),
  auditRequest: (id: string) => landlordApi.get(`/api/texas/ccpa/dsr/requests/${id}/audit`),
  sweepDeadlines: () => landlordApi.post('/api/texas/ccpa/dsr/deadlines/sweep'),
};
